import { UndefinedInitialDataInfiniteOptions, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { PaginationResponse } from "@/interfaces/api";
import { handleAppError } from "@/utils";

import { useDepartmentRoute } from "./use-department-route";

export interface UseInfiniteFetchOptions<T>
  extends Omit<UndefinedInitialDataInfiniteOptions<T>, "queryFn" | "getNextPageParam" | "initialPageParam"> {
  toastOnError?: boolean;
  treatNullAsError?: boolean;
  includeDepartmentInCache?: boolean;
}

const DEFAULT_QUERY_KEY = ["use-infinite-fetch"];

const defaultOptions = { queryKey: DEFAULT_QUERY_KEY, toastOnError: true, includeDepartmentInCache: false };

export function useInfiniteFetch<T>(
  callback: (pageParam: number) => Promise<PaginationResponse<T>>,
  options: UseInfiniteFetchOptions<PaginationResponse<T>> = defaultOptions,
) {
  options = { ...defaultOptions, ...options };

  const { department } = useDepartmentRoute();

  // Se includeDepartmentInCache for true, adicionar department no início da queryKey
  const queryKey =
    options.includeDepartmentInCache && department ? [...(options.queryKey || []), department] : options.queryKey;

  const result = useInfiniteQuery({
    ...options,
    queryKey,
    retry: false,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => callback(pageParam as number),
    getNextPageParam: (lastPage) => {
      if (!lastPage || !Array.isArray(lastPage.data) || typeof lastPage.page !== "number") {
        return undefined;
      }

      // Se a página atual é menor que o total de páginas, retorna a próxima página
      if (lastPage.page < lastPage.pageCount) {
        return lastPage.page + 1;
      }
      return undefined; // Não há mais páginas
    },
  });

  const isNull = useMemo(() => {
    return !!options.treatNullAsError && result.data === null;
  }, [options.treatNullAsError, result.data]);

  useEffect(() => {
    if (options.toastOnError && (result.error || isNull)) {
      handleAppError(result.error, { handleUnknownError: true });
    }
  }, [result.error, options.toastOnError, isNull]);

  return {
    ...result,
    isPendingOrErrored: result.isError || result.isLoading || isNull,
  };
}
